import React, { useState, useEffect, useMemo, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import ReactDOM from 'react-dom'
import './SelectLeague.css'

const SearchForPlayer = () => {
	const [isLoading, setIsLoading] = useState(false)
	const [loadedAllPlayers, setLoadedAllPlayers] = useState([])
	const [searchTerm, setSearchTerm] = useState('')
	const [filteredResults, setFilteredResults] = useState([])
	const [error, setError] = useState()

	const navigate = useNavigate()

	// refs for positioning portal dropdown
	const inputRef = useRef(null)
	const dropdownRef = useRef(null)
	const [dropdownPos, setDropdownPos] = useState(null)

	useEffect(() => {
		const sendRequest = async () => {
			setIsLoading(true)
			try {
				const response = await fetch(
					process.env.REACT_APP_BACKEND_URL + '/admin/getAllPlayers',
				)
				const responseData = await response.json()
				if (!response.ok) throw new Error(responseData.message)
				setLoadedAllPlayers(responseData.allPlayers || [])
			} catch (err) {
				setError(err.message)
			} finally {
				setIsLoading(false)
			}
		}
		sendRequest()
	}, [])

	// Build display strings: "Last, First" only
	const playerArray = useMemo(() => {
		if (!loadedAllPlayers?.length) return []
		const sorted = [...loadedAllPlayers].sort(
			(a, b) =>
				(a.lastName || '').localeCompare(b.lastName || '') ||
				(a.firstName || '').localeCompare(b.firstName || ''),
		)
		return sorted.map((p) => `${p.lastName}, ${p.firstName}`)
	}, [loadedAllPlayers])

	useEffect(() => {
		if (!searchTerm || searchTerm.length < 3) {
			setFilteredResults([])
			return
		}
		const term = searchTerm.toLowerCase()
		const results = playerArray.filter((p) => p.toLowerCase().includes(term))
		setFilteredResults(results.slice(0, 60))
	}, [searchTerm, playerArray])

	const closeDropdown = () => setFilteredResults([])

	const computeDropdownPos = () => {
		const el = inputRef.current
		if (!el) return
		const r = el.getBoundingClientRect()
		setDropdownPos({
			left: r.left + window.scrollX,
			top: r.bottom + window.scrollY + 4,
			width: r.width,
		})
	}

	// Whenever results show, position the dropdown
	useEffect(() => {
		if (filteredResults.length > 0) computeDropdownPos()
	}, [filteredResults.length])

	// Keep dropdown aligned on scroll/resize
	useEffect(() => {
		const onScroll = () => {
			if (filteredResults.length > 0) computeDropdownPos()
		}
		const onResize = () => {
			if (filteredResults.length > 0) computeDropdownPos()
		}

		window.addEventListener('scroll', onScroll, true)
		window.addEventListener('resize', onResize)
		return () => {
			window.removeEventListener('scroll', onScroll, true)
			window.removeEventListener('resize', onResize)
		}
	}, [filteredResults.length])

	const handleItemClick = (item) => {
		// item is "Last, First"
		const [lastName, firstName] = item.split(', ').map((s) => s?.trim())

		const found = loadedAllPlayers.find((p) => {
			const ln = (p.lastName || '').trim()
			const fn = (p.firstName || '').trim()
			return (
				ln.toLowerCase() === (lastName || '').toLowerCase() &&
				fn.toLowerCase() === (firstName || '').toLowerCase()
			)
		})

		if (found?._id) {
			navigate(`/${found._id}/history`)
			setSearchTerm('')
			closeDropdown()
		}
	}

	useEffect(() => {
		const onClick = (e) => {
			const inputEl = inputRef.current
			const ddEl = dropdownRef.current

			// If click is inside input OR inside dropdown, do nothing
			if (inputEl && inputEl.contains(e.target)) return
			if (ddEl && ddEl.contains(e.target)) return

			// Otherwise close
			closeDropdown()
		}

		document.addEventListener('click', onClick)
		return () => document.removeEventListener('click', onClick)
	}, [])

	return (
		<React.Fragment>
			<div style={{ marginBottom: '2rem', overflowX: 'hidden' }}>
				{!isLoading && (
					<div className='row'>
						<div className='col'></div>

						<div className='col-sm'>
							<input
								ref={inputRef}
								className='searchInput'
								type='text'
								placeholder='Enter player name to search'
								value={searchTerm}
								onChange={(e) => setSearchTerm(e.target.value)}
								onFocus={() => {
									if (filteredResults.length > 0) computeDropdownPos()
								}}
							/>

							{searchTerm.length >= 3 && filteredResults.length === 0 && (
								<p style={{ textAlign: 'center' }}>No results found.</p>
							)}
						</div>

						<div className='col'></div>
					</div>
				)}
			</div>

			{/* PORTAL DROPDOWN (renders above everything, never clipped) */}
			{filteredResults.length > 0 &&
				dropdownPos &&
				ReactDOM.createPortal(
					<ul
						ref={dropdownRef}
						className='playerSearchDropdownPortal'
						style={{
							position: 'absolute',
							left: dropdownPos.left,
							top: dropdownPos.top,
							width: dropdownPos.width,
							zIndex: 999999,
						}}
					>
						{filteredResults.map((item) => (
							<li
								key={item}
								className='playerSearchResult'
								onMouseDown={(e) => {
									e.preventDefault() // prevents input blur weirdness
									handleItemClick(item)
								}}
							>
								{item}
							</li>
						))}
					</ul>,
					document.body,
				)}
		</React.Fragment>
	)
}

export default SearchForPlayer
